import { AlertCircle, ArrowLeft, FileUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { ListMyOffers } from '@cold-ambulance/client';
import {
  type TimelineEvent,
  getAnyDocumentRejectionReason,
  getIncompleteProfileTab,
  hasAnyDocumentRejected,
} from '@cold-ambulance/isomorphic';
import { buttonVariants } from '@cold-ambulance/shadcn';
import {
  AwaitingSignatureBanner,
  EmptySection,
  HomeCard,
  IncompleteProfileBanner,
  PendingDocumentsBanner,
  PendingVerificationBanner,
  RejectedAccountBanner,
  useData,
} from '@cold-ambulance/ui';

import { useUser } from '../firebase.tsx';
import { InProgressRequestCard } from './requests/in-progress-request-card.tsx';
import { RequestCard, RequestCardSkeleton } from './requests/request-card.tsx';
import { UpdatedRequestCard } from './requests/updated-request-card.tsx';

export default function Home() {
  const { t } = useTranslation();
  const userQuery = useUser();
  const incompleteProfileTab = getIncompleteProfileTab(userQuery.data);
  const requestsQuery = useData('GET /requests', {
    pageSize: 2,
  });
  const inProgressRequestQuery = useData('GET /me/offers', {
    pageSize: 10,
    status: 'IN_PROGRESS',
  });
  const pendingDocsQuery = useData('GET /me/offers', {
    pageSize: 10,
    status: 'PENDING',
  });

  // Filter offers in document phase (selected by customer + has required documents)
  const offersInDocumentPhase =
    pendingDocsQuery.data?.records.filter(
      (offer) =>
        offer.request.offerId === offer.id &&
        offer.request.requiredDocuments.length > 0,
    ) ?? [];

  // Separate rejected (urgent) from normal pending - derive from timeline
  // Only check current documents (ignore deleted/replaced ones)
  const rejectedOffers = offersInDocumentPhase.filter((it) =>
    hasAnyDocumentRejected(
      it.timeline as TimelineEvent[],
      it.documents.map((d) => d.id),
    ),
  );
  const pendingOffers = offersInDocumentPhase.filter(
    (it) =>
      !hasAnyDocumentRejected(
        it.timeline as TimelineEvent[],
        it.documents.map((d) => d.id),
      ) && it.documents.length < it.request.requiredDocuments.length,
  );

  return (
    <div className="page-container">
      <div className="mx-auto w-full">
        <ContractReadyBanner />
        {incompleteProfileTab && (
          <IncompleteProfileBanner className="mb-6" to={`/profile?tab=${incompleteProfileTab}`} />
        )}
        {(rejectedOffers.length > 0 || pendingOffers.length > 0) && (
          <div className="mb-6 space-y-4">
            {rejectedOffers.map((offer) => (
              <PendingOfferDocumentsBanner
                key={offer.id}
                offer={offer}
                variant="rejected"
              />
            ))}
            {pendingOffers.map((offer) => (
              <PendingOfferDocumentsBanner
                key={offer.id}
                offer={offer}
                variant="pending"
              />
            ))}
          </div>
        )}

        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-bold">{t('home.inProgressRequests')}</h1>
          <p className="text-sm text-[#6B7280]">{t('home.currentTasks')}</p>
        </div>

        {inProgressRequestQuery.isLoading ? (
          <RequestsSkeleton />
        ) : inProgressRequestQuery.data?.records.length ? (
          <div className="mt-8 flex flex-col gap-4">
            {inProgressRequestQuery.data.records.slice(0, 5).map((offer) => (
              <InProgressRequestCard key={offer.id} request={offer.request} />
            ))}
          </div>
        ) : (
          <Intro />
        )}
        {requestsQuery.isLoading ? (
          <RequestsSkeleton />
        ) : requestsQuery.data?.records.length ? (
          <RequestsListSection />
        ) : (
          <EmptySection
            title={t('home.latestRequests')}
            subtitle={t('home.noCurrentRequests')}
            description={t('home.noRequestsReceived')}
          />
        )}
      </div>
    </div>
  );
}

function Intro() {
  const { t } = useTranslation();
  const userQuery = useUser();
  const notificationsQuery = useData('GET /notifications', {
    pageSize: 10,
  });

  const approved = userQuery.data?.approved;
  const hasPendingDocuments = (userQuery.data?.pendingDocumentsCount ?? 0) > 0;

  const rejectionNotification = notificationsQuery.data?.records.find(
    (n) => n.type === 'VENDOR_REJECTED',
  );
  const rejectionReason = (
    rejectionNotification?.metadata as { reason?: string } | null | undefined
  )?.reason;

  return (
    <>
      {approved === null && <PendingVerificationBanner />}
      {approved === false && (
        <RejectedAccountBanner reason={rejectionReason} />
      )}
      {hasPendingDocuments && <PendingDocumentsBanner />}
      <div className="flex w-full flex-row justify-center">
        <p className="mt-8 mb-4 font-medium">{t('home.noInProgressRequests')}</p>
      </div>
    </>
  );
}

function RequestsListSection() {
  const { t } = useTranslation();
  const requestsQuery = useData('GET /vendor/requests', {
    pageSize: 2,
  });

  return (
    <>
      <div className="flex w-auto items-center justify-between">
        <p className="mt-8 mb-7 font-medium">{t('home.latestRequests')}</p>
        <Link
          to="/requests"
          className="text-primary flex gap-x-1 text-sm font-bold"
        >
          <span>{t('common.viewAll')}</span>
          <ArrowLeft />
        </Link>
      </div>
      <div className="flex flex-row gap-4">
        {requestsQuery.data?.records.map((request) => (
          <UpdatedRequestCard key={request.id} request={request} />
        ))}
      </div>
    </>
  );
}

function RequestsSkeleton() {
  const { t } = useTranslation();
  return (
    <>
      <p className="mt-8 mb-7 font-medium">{t('home.latestRequests')}</p>
      <div className="grid grid-cols-2 gap-4">
        <RequestCardSkeleton />
        <RequestCardSkeleton />
      </div>
    </>
  );
}

function PendingOfferDocumentsBanner({
  offer,
  variant,
}: {
  offer: ListMyOffers['records'][number];
  variant: 'rejected' | 'pending';
}) {
  const { t } = useTranslation();
  const isRejected = variant === 'rejected';
  const uploadedCount = offer.documents.length;
  const totalCount = offer.request.requiredDocuments.length;
  // Derive rejection reason from timeline (only check current documents)
  const currentDocumentIds = offer.documents.map((d) => d.id);
  const rejectionReason = isRejected
    ? getAnyDocumentRejectionReason(
        offer.timeline as TimelineEvent[],
        currentDocumentIds,
      )
    : null;

  return (
    <div
      className={`flex items-center justify-between rounded-xl border py-3 ps-7 pe-4 ${
        isRejected ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'
      }`}
    >
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <AlertCircle
            size={20}
            className={isRejected ? 'text-red-600' : 'text-amber-600'}
          />
          <p
            className={`text-sm font-medium ${isRejected ? 'text-red-800' : 'text-amber-800'}`}
          >
            {isRejected
              ? t('home.documentsRejected')
              : t('home.offerAwaitingDocs')}
          </p>
        </div>
        <div
          className={`ms-7 mt-1 text-sm ${isRejected ? 'text-red-700' : 'text-amber-700'}`}
        >
          <span>{t('home.project', { name: offer.request.projectName })}</span>
          {isRejected && rejectionReason ? (
            <span className="block">{t('home.reason', { reason: rejectionReason })}</span>
          ) : (
            <span className="ms-2">
              • {t('home.documentsUploaded', { uploaded: uploadedCount, total: totalCount })}
            </span>
          )}
        </div>
      </div>
      <Link
        to={`/offers/${offer.id}/documents`}
        className={buttonVariants({
          variant: 'link',
          className: isRejected
            ? 'bg-red-600 text-sm text-white hover:bg-red-700'
            : 'bg-amber-600 text-sm text-white hover:bg-amber-700',
        })}
      >
        <FileUp size={16} className="me-1" />
        {isRejected ? t('home.reUpload') : t('home.uploadDocuments')}
      </Link>
    </div>
  );
}

function ContractReadyBanner() {
  const { t } = useTranslation();
  const notificationsQuery = useData('GET /notifications', {
    read: false,
    page: 1,
    pageSize: 10,
  });

  const contractReadyNotifications =
    notificationsQuery.data?.records.filter(
      (it) => it.type === 'CONTRACT_READY',
    ) ?? [];

  if (contractReadyNotifications.length === 0) {
    return null;
  }

  return (
    <div className="mb-6 space-y-4">
      {contractReadyNotifications.map((notification) => {
        const metadata = notification.metadata as {
          projectName?: string;
        } | null;
        return (
          <AwaitingSignatureBanner
            key={notification.id}
            projectName={metadata?.projectName ?? t('home.defaultProject')}
            linkUrl={notification.actionUrl || '/offers'}
          />
        );
      })}
    </div>
  );
}
